/**
 * Type-safe deep linking utilities for OAuth redirect flow
 *
 * @see ADR-Notes.md - Deep Linking Approach
 *
 * URL Structure:
 * - Path: Route location
 * - Search params: Shared client-server state (filters, pagination, form pre-fill)
 * - Hash: Client-only state (scroll positions, UI triggers)
 *
 * OAuth Flow:
 * 1. User visits: /signin?destination=<encoded_destination>
 * 2. OAuth provider callback includes destination in state
 * 3. Callback handler decodes destination and 307 redirects
 * 4. Destination page parses hash and executes client-side actions
 */

// ============================================================================
// Hash Actions - Client-only state triggers
// ============================================================================

/**
 * Hash actions for listing pages
 * Examples: #place-bid, #favorite, #scroll-to:comment:abc123, #scroll-to:reply:xyz789, #media
 *
 * Generic scroll-to pattern: scroll-to:{entityType}:{entityId}
 * Uses colons to avoid ambiguity with UUIDs that contain hyphens
 * Supports any entity type: comment, reply, bid, notification, etc.
 */
export type ListingHashAction =
  | "place-bid"
  | "favorite"
  | "media"
  | "details"
  | "comments"
  | `scroll-to:${string}:${string}`;

/**
 * Hash actions for browse page
 * Examples: #map-view, #card-view, #table-view
 * Note: Primary view state should use search params (?view=map|card|table)
 * Hash actions are for client-side UI state (e.g., temporary view changes)
 */
export type BrowseHashAction = "map-view" | "card-view" | "table-view";

/**
 * All supported hash actions across the application
 */
export type HashAction = ListingHashAction | BrowseHashAction;

/**
 * Parse hash action from URL hash fragment
 * @example parseHashAction("#place-bid") → "place-bid"
 * @example parseHashAction("#scroll-to-comment-abc123") → "scroll-to-comment-abc123"
 */
export function parseHashAction(hash: string): HashAction | null {
  if (!hash) return null;
  // Remove leading # if present
  const cleaned = hash.startsWith("#") ? hash.slice(1) : hash;
  return cleaned as HashAction;
}

/**
 * Extract entity type and ID from generic scroll-to hash actions
 *
 * Pattern: scroll-to:{entityType}:{entityId}
 * Uses colons to avoid ambiguity with UUIDs that contain hyphens
 *
 * @example extractScrollTargetId("scroll-to:comment:abc123") → { type: "comment", id: "abc123" }
 * @example extractScrollTargetId("scroll-to:reply:01234567-89ab-cdef-0123-456789abcdef") → { type: "reply", id: "01234567-89ab-cdef-0123-456789abcdef" }
 * @example extractScrollTargetId("scroll-to:bid:def456") → { type: "bid", id: "def456" }
 * @example extractScrollTargetId("scroll-to:notification:ghi789") → { type: "notification", id: "ghi789" }
 */
export function extractScrollTargetId(
  hashAction: string,
): { type: string; id: string } | null {
  // Generic pattern: scroll-to:{entityType}:{entityId}
  // Uses colons as separators to avoid confusion with UUID hyphens
  const match = hashAction.match(/^scroll-to:([^:]+):(.+)$/);

  if (match && match[1] && match[2]) {
    return {
      type: match[1], // First captured group: entity type
      id: match[2], // Second captured group: entity ID (can contain hyphens)
    };
  }

  return null;
}

// ============================================================================
// Search Params - Shared client-server state
// ============================================================================

/**
 * Search params for /browse page
 */
export type BrowseSearchParams = {
  view?: "map" | "card" | "table"; // View mode
  sort?: `${string}:${"asc" | "desc"}`;
  page?: string;
  filters?: string; // JSON-encoded filter object
  bounds?: string; // Map viewport bounds
};

/**
 * Search params for /listing/[slug]/[id] pages
 */
export type ListingSearchParams = {
  // utm_source?: string;
  // utm_medium?: string;
  // utm_campaign?: string;
  // ref?: string; // Referral code
};

/**
 * All supported search params types
 */
export type SearchParamsMap = {
  "/browse": BrowseSearchParams;
  "/listing": ListingSearchParams;
};

// ============================================================================
// Destination URL Builder
// ============================================================================

/**
 * Build a destination URL with type-safe search params and hash actions
 *
 * @example
 * ```typescript
 * // Listing with hash action
 * buildDestinationUrl("/listing/luxury-apartment/abc123", undefined, "place-bid")
 * // → "/listing/luxury-apartment/abc123#place-bid"
 *
 * // Browse with search params
 * buildDestinationUrl("/browse", { sort: "price:asc", page: "2" })
 * // → "/browse?sort=price:asc&page=2"
 *
 * // Combined: path + params + hash
 * buildDestinationUrl("/listing/downtown-loft/xyz789", { ref: "email" }, "scroll-to-comment-abc")
 * // → "/listing/downtown-loft/xyz789?ref=email#scroll-to-comment-abc"
 * ```
 */
export function buildDestinationUrl<T extends Record<string, any>>(
  path: string,
  searchParams?: T,
  hashAction?: HashAction,
): string {
  let url = path;

  // Add search params
  if (searchParams && Object.keys(searchParams).length > 0) {
    const params = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });
    const paramString = params.toString();
    if (paramString) {
      url += `?${paramString}`;
    }
  }

  // Add hash action
  if (hashAction) {
    url += `#${hashAction}`;
  }

  return url;
}

/**
 * Parse destination URL into components
 * Separates path, search params, and hash action for processing
 */
export function parseDestinationUrl(destinationUrl: string): {
  path: string;
  searchParams: Record<string, string>;
  hashAction: HashAction | null;
} {
  try {
    // Handle relative URLs
    const baseUrl =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://app.apartmentdibs.com";
    const url = new URL(destinationUrl, baseUrl);

    const searchParams: Record<string, string> = {};
    url.searchParams.forEach((value, key) => {
      searchParams[key] = value;
    });

    return {
      path: url.pathname,
      searchParams,
      hashAction: parseHashAction(url.hash),
    };
  } catch {
    // Fallback for malformed URLs
    const [pathAndParams = "", hash] = destinationUrl.split("#");
    const [path = "/", paramString] = pathAndParams.split("?");

    const searchParams: Record<string, string> = {};
    if (paramString) {
      const params = new URLSearchParams(paramString);
      params.forEach((value, key) => {
        searchParams[key] = value;
      });
    }

    return {
      path: path || "/",
      searchParams,
      hashAction: hash ? parseHashAction(hash) : null,
    };
  }
}

// ============================================================================
// OAuth Flow Helpers
// ============================================================================

/**
 * Encode destination URL for OAuth flow using Base64 URL encoding
 *
 * Base64 URL encoding is URL-safe and doesn't require additional encoding:
 * - Replaces `+` with `-`
 * - Replaces `/` with `_`
 * - Removes padding `=`
 *
 * This allows destinations to be used directly in query parameters without
 * additional URL encoding (e.g., `/signin?destination=<base64url>`)
 *
 * @param destinationUrl - Destination URL to encode
 * @returns Base64 URL encoded string
 *
 * @example
 * ```typescript
 * encodeDestination("/listing/abc#bid")
 * // → "L2xpc3RpbmcvYWJjI2JpZA" (no padding, URL-safe)
 * ```
 */
export function encodeDestination(destinationUrl: string): string {
  // Standard base64 encode
  const base64 = Buffer.from(destinationUrl).toString("base64");

  // Convert to base64url: replace +/= with -_<nothing>
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); // Remove padding
}

/**
 * Decode destination URL from OAuth callback
 * Handles both standard base64 and base64url encoding
 *
 * @param encodedDestination - Base64 or Base64 URL encoded string
 * @returns Decoded destination URL
 *
 * @example
 * ```typescript
 * decodeDestination("L2xpc3RpbmcvYWJjI2JpZA")
 * // → "/listing/abc#bid"
 *
 * // Also handles standard base64 (with padding)
 * decodeDestination("L2xpc3RpbmcvYWJjI2JpZA==")
 * // → "/listing/abc#bid"
 * ```
 */
export function decodeDestination(encodedDestination: string): string {
  try {
    // Convert base64url to standard base64
    let base64 = encodedDestination.replace(/-/g, "+").replace(/_/g, "/");

    // Add padding if needed (base64 strings should be multiple of 4)
    const padding = (4 - (base64.length % 4)) % 4;
    if (padding > 0) {
      base64 += "=".repeat(padding);
    }

    return Buffer.from(base64, "base64").toString("utf-8");
  } catch {
    return "";
  }
}

/**
 * Build signin URL with destination
 * @example buildSigninUrl("/listing/abc123#place-bid") → "/signin?destination=%2Flisting%2Fabc123%23place-bid"
 */
export function buildSigninUrl(destinationUrl: string): string {
  return `/signin?destination=${encodeDestination(destinationUrl)}`;
}

/**
 * Build destination URL from current browser location
 * Captures current path, search params, and hash for OAuth redirect
 *
 * Useful for auth guards and signin buttons that want to redirect back
 * to the current page after authentication.
 *
 * @example
 * ```typescript
 * // Current URL: /listing/abc123?ref=email#place-bid
 * const destination = buildDestinationFromCurrentLocation()
 * // → "/listing/abc123?ref=email#place-bid"
 *
 * const signinUrl = buildSigninUrl(destination)
 * // → "/signin?destination=<base64-encoded-url>"
 * ```
 *
 * @returns Full destination URL with path + search + hash
 */
export function buildDestinationFromCurrentLocation(): string {
  if (typeof window === "undefined") {
    return "/";
  }

  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

/**
 * Normalize destination URL from multiple encoding formats
 *
 * Supports three formats with auto-detection:
 * 1. Raw relative URL: `/foo?bar=baz#hash` (starts with `/`)
 * 2. URL-encoded: `%2Ffoo%3Fbar%3Dbaz%23hash` (starts with `%2F`)
 * 3. Base64 URL-encoded: `L2Zvbz9iYXI9YmF6I2hhc2g` (URL-safe base64, no padding)
 *
 * If destination is null/undefined, returns empty string.
 * If destination is invalid, returns "/" as safe fallback.
 *
 * @example
 * ```typescript
 * normalizeDestination("/listing/abc#bid")
 * // → "/listing/abc#bid"
 *
 * normalizeDestination("%2Flisting%2Fabc%23bid")
 * // → "/listing/abc#bid"
 *
 * normalizeDestination("L2xpc3RpbmcvYWJjI2JpZA")
 * // → "/listing/abc#bid" (base64url, no padding)
 *
 * normalizeDestination(null)
 * // → window.location.pathname + search + hash (if available)
 * ```
 *
 * @param destination - Destination in any supported format
 * @returns Normalized relative URL starting with "/"
 */
export function normalizeDestination(
  destination: string | null | undefined,
): string {
  // If no destination provided, use current location
  if (!destination) {
    return "";
  }

  // Format 1: Raw relative URL (starts with /)
  if (destination.startsWith("/")) {
    return destination;
  }

  // Format 2: URL-encoded (starts with %2F which is encoded /)
  if (destination.startsWith("%2F") || destination.startsWith("%2f")) {
    try {
      const decoded = decodeURIComponent(destination);
      if (decoded.startsWith("/")) {
        return decoded;
      }
    } catch {
      // Fall through to base64 attempt
    }
  }

  // Format 3: Base64 URL-encoded (fallback)
  // Try to decode as base64url (handles both standard base64 and base64url)
  try {
    const decoded = decodeDestination(destination);
    // Verify it's a valid relative URL
    if (decoded && decoded.startsWith("/")) {
      return decoded;
    }
    // If base64 decode succeeded but doesn't start with /, try URL decode
    if (decoded && (decoded.startsWith("%2F") || decoded.startsWith("%2f"))) {
      const urlDecoded = decodeURIComponent(decoded);
      if (urlDecoded.startsWith("/")) {
        return urlDecoded;
      }
    }
  } catch {
    // Base64 decode failed, invalid format
  }

  // Invalid format - return safe fallback
  console.error(
    `[deep-linking] Invalid destination format: "${destination}". Using "/" as fallback.`,
  );
  return "/";
}

// ============================================================================
// Type-safe Route Builders
// ============================================================================

/**
 * Build typed destination URLs for specific routes
 */
export const destinations = {
  /**
   * Browse page with optional view, filters, sorting, and hash actions
   * View state uses search params: ?view=map|card|table
   * Hash actions for client-side UI state: #map-view, #card-view, #table-view
   * @example destinations.browse({ view: "map", sort: "price:asc" }) → "/browse?view=map&sort=price:asc"
   * @example destinations.browse(undefined, "map-view") → "/browse#map-view"
   */
  browse: (params?: BrowseSearchParams, hashAction?: BrowseHashAction) =>
    buildDestinationUrl("/browse", params, hashAction),

  /**
   * Listing page with optional UTM params and hash actions
   */
  listing: (
    slug: string,
    id: string,
    params?: ListingSearchParams,
    hashAction?: ListingHashAction,
  ) => buildDestinationUrl(`/listing/${slug}/${id}`, params, hashAction),

  /**
   * Home page
   */
  home: () => "/",

  /**
   * Landlord dashboard
   */
  landlord: () => "/landlord",
} as const;

/**
 * Example usage:
 *
 * ```typescript
 * // In a link component
 * import { destinations, buildSigninUrl } from "@/lib/deep-linking";
 *
 * const destination = destinations.listing(
 *   "luxury-apartment-downtown",
 *   "abc123",
 *   { ref: "email-campaign" },
 *   "place-bid"
 * );
 *
 * const signinUrl = buildSigninUrl(destination);
 * // → /signin?destination=%2Flisting%2Fluxury-apartment-downtown%2Fabc123%3Fref%3Demail-campaign%23place-bid
 *
 * // In the listing page component
 * import { parseHashAction, extractScrollTargetId } from "@/lib/deep-linking";
 *
 * useEffect(() => {
 *   const hashAction = parseHashAction(window.location.hash);
 *   if (hashAction === "place-bid") {
 *     openBidModal();
 *   } else if (hashAction?.startsWith("scroll-to-")) {
 *     const target = extractScrollTargetId(hashAction);
 *     if (target) {
 *       scrollToElement(target.type, target.id);
 *     }
 *   }
 * }, []);
 * ```
 */
